// Tipos del contrato. Se escriben a mano en esta fase; el cliente generado
// desde OpenAPI llega cuando haya un generador en la cadena de build.

export type RoleKey =
  | "superadmin"
  | "tenant_admin"
  | "developer"
  | "billing_viewer"
  | "read_only";

export type Tenant = {
  id: string;
  name: string;
  slug: string;
  status: "active" | "suspended" | "closed";
  kill_switch: boolean;
  parent_tenant_id: string | null;
  created_at: string;
  /** Solo viene si la cuenta está suspendida con motivo registrado. */
  suspended_reason?: string;
};

/**
 * Usuario recién dado de alta en una cuenta desde la plataforma.
 *
 * `temporary_password` solo viene si el usuario es nuevo: a uno que ya existía
 * no se le cambia la contraseña, porque puede operar otras cuentas con ella.
 */
export type TenantUserCreated = User & { temporary_password?: string };

/**
 * Lo que devuelve restablecer la contraseña de un usuario.
 *
 * La temporal viene **siempre** aquí, y una sola vez: a diferencia del alta,
 * restablecer es explícitamente pedir una contraseña nueva para alguien.
 */
export type PasswordReset = {
  id: string;
  email: string;
  temporary_password: string;
  sessions_closed: boolean;
};

/** Lo que devuelve el borrado de datos de una cuenta: conteos reales. */
export type ErasureResult = {
  tenant_id: string;
  deleted: Record<string, number>;
  kept: string[];
};

export type User = {
  id: string;
  email: string;
  full_name: string;
  role: RoleKey;
  status: "invited" | "active" | "disabled";
  mfa_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
};

export type Session = {
  user: {
    id: string;
    email: string;
    full_name: string;
    status: string;
    is_superadmin: boolean;
    mfa_enabled: boolean;
    last_login_at: string | null;
  };
  active_tenant_id: string;
  role: RoleKey | "";
  permissions: string[];
  tenants: { tenant: Tenant; role: RoleKey }[];
};

export type VoiceApp = {
  id: string;
  name: string;
  /** De dónde salen las instrucciones; las dos fuentes se excluyen. */
  source: "voice_url" | "markup";
  voice_url: string | null;
  /** IAMEDIA Markup guardado. La plataforma lo ejecuta sin salir a la red. */
  markup: string | null;
  voice_method: "POST" | "GET";
  fallback_url: string | null;
  status_callback_url: string | null;
  status_callback_events: string[];
  timeout_ms: number;
  /** Graba las llamadas de esta aplicación. Apagado por omisión. */
  recording_enabled: boolean;
  status: "active" | "disabled";
  created_at: string;
} & AMDConfig;

/** Detección de contestadora. Los nueve parámetros son los de `app_amd` de Asterisk. */
export type AMDConfig = {
  machine_detection: "off" | "enable" | "detect_message_end";
  machine_policy: "hangup" | "continue" | "continue_and_notify";
  amd_initial_silence_ms: number;
  amd_greeting_ms: number;
  amd_after_greeting_silence_ms: number;
  amd_total_analysis_ms: number;
  amd_min_word_ms: number;
  amd_between_words_silence_ms: number;
  amd_max_words: number;
  amd_silence_threshold: number;
  amd_max_word_ms: number;
};

export type Recording = {
  id: string;
  call_id: string;
  attempt_id: string;
  status: "recording" | "completed" | "failed" | "purged";
  format: string;
  /**
   * Medida del archivo, no copiada de la duración de la llamada: MixMonitor
   * puede arrancar tarde, y entonces los dos números no coinciden.
   */
  duration_seconds: number | null;
  bytes: number | null;
  /** Liga firmada; sólo viene cuando el estado es `completed`. */
  recording_url: string | null;
  recording_url_expires_at: string | null;
  started_at: string;
  ended_at: string | null;
};

export type ApiKey = {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  expires_at: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  created_at: string;
  /** Solo viene en la respuesta de creación, una única vez. */
  secret?: string;
};

export type PhoneNumber = {
  id: string;
  e164: string;
  country: string;
  number_type: "local" | "mobile" | "tollfree";
  /** `assigned` es el estado normal; `available` es inventario sin dueño. */
  status: "available" | "assigned" | "suspended";
  tenant_id: string | null;
  voice_app_id: string | null;
  voice_url: string | null;
  fallback_url: string | null;
};

export type CallerId = {
  id: string;
  e164: string;
  status: "pending" | "verified" | "rejected";
  verified_at: string | null;
  created_at: string;
};

/**
 * Respuesta de alta de un caller ID.
 *
 * `verification_call_queued` es la diferencia entre "ya va a sonar" y "quedó
 * registrado pero nadie va a recibir el código". El 201 es el mismo en los dos
 * casos porque el registro sí se hizo, así que la UI tiene que mirar esta
 * bandera para no prometer una llamada que no se encoló.
 */
export type CallerIdCreated = CallerId & {
  verification_call_queued: boolean;
  verification_call_id?: string;
};

export type SigningSecret = {
  id: string;
  status: "active" | "rotating" | "retired";
  activated_at: string;
  retires_at: string | null;
  secret?: string;
};

export type Trunk = {
  id: string;
  node_id: string;
  name: string;
  endpoint_name: string;
  direction: "inbound" | "outbound" | "both";
  max_cps: number;
  max_concurrent: number;
  status: "active" | "disabled";
  /** Dígitos que se quitan del E.164 antes de marcar a este carrier. */
  dial_strip_digits: number;
  /** Lo que se antepone al número ya recortado. */
  dial_prefix: string;
};

export type Node = {
  id: string;
  name: string;
  status: "active" | "draining" | "down";
  capacity_calls: number;
  active_calls: number;
  weight: number;
  last_seen_at: string | null;
};

// ------------------------------------------------------------- llamadas ----

export type CallStatus =
  | "queued"
  | "initiated"
  | "ringing"
  | "in-progress"
  | "completed"
  | "busy"
  | "no-answer"
  | "failed"
  | "canceled";

export type CallDirection = "outbound" | "inbound";

export type Call = {
  id: string;
  direction: CallDirection;
  status: CallStatus;
  from: string;
  to: string;
  voice_app_id: string | null;
  voice_url: string | null;
  fallback_url: string | null;
  max_duration_seconds: number;
  answered_seconds: number;
  billable_seconds: number;
  amd_requested: boolean;
  machine_detection: string | null;
  end_reason: string | null;
  metadata: Record<string, unknown>;
  queued_at: string;
  ended_at: string | null;
};

export type CallAttempt = {
  id: string;
  call_id: string;
  node_id: string | null;
  trunk_id: string | null;
  dial_status: string | null;
  hangup_cause: number | null;
  sip_code: number | null;
  amd_status: string | null;
  amd_cause: string | null;
  answered_seconds: number;
  billable_seconds: number;
  end_reason: string | null;
  queued_at: string;
  originated_at: string | null;
  progress_at: string | null;
  progress_signal: "ringing" | "progress" | "proceeding" | null;
  ringing_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
};

// Una pata saliente por cada `<Dial>`. No trae nombres de canal: la API no los
// expone (CLAUDE.md §8).
export type CallLeg = {
  id: string;
  attempt_id: string;
  role: string;
  destination: string | null;
  trunk_id: string | null;
  caller_id: string | null;
  dial_status: string | null;
  // Timbrado más conversación, lo que se puede medir desde la llamada.
  dial_seconds: number;
  // Conversación de la pata, derivada de eventos AMI. 0 = todavía no se sabe.
  answered_seconds: number;
  time_limit_seconds: number | null;
  created_at: string;
  answered_at: string | null;
  ended_at: string | null;
};

export type CallEvent = {
  id: string;
  type: string;
  occurred_at: string;
  data: Record<string, unknown>;
};

// ------------------------------------------------------------- webhooks ----

export type EventType =
  | "call.queued"
  | "call.initiated"
  | "call.progressing"
  | "call.ringing"
  | "call.answered"
  | "call.completed"
  | "call.failed"
  | "stream.started"
  | "stream.stopped"
  | "balance.low";

export const EVENT_TYPES: EventType[] = [
  "call.queued",
  "call.initiated",
  "call.progressing",
  "call.ringing",
  "call.answered",
  "call.completed",
  "call.failed",
  "stream.started",
  "stream.stopped",
  "balance.low",
];

export type WebhookEndpoint = {
  id: string;
  url: string;
  description: string;
  /** Vacío significa todos los tipos. */
  events: EventType[];
  status: "active" | "disabled";
  last_success_at: string | null;
  last_failure_at: string | null;
  consecutive_failures: number;
  created_at: string;
};

export type WebhookDeliveryStatus = "pending" | "delivering" | "delivered" | "dead";

export type WebhookDelivery = {
  id: string;
  event_id: string;
  event_type: EventType;
  webhook_endpoint_id: string | null;
  url: string;
  status: WebhookDeliveryStatus;
  attempts: number;
  next_attempt_at: string | null;
  last_status_code: number | null;
  last_error: string | null;
  latency_ms: number | null;
  delivered_at: string | null;
  created_at: string;
};

/** Una entrega con el cuerpo que recibió el endpoint. */
export type WebhookDeliveryDetail = WebhookDelivery & {
  /** El sobre entregado. `null` si la retención ya se llevó el evento. */
  body: Record<string, unknown> | null;
  body_unavailable_reason?: string;
};

// --------------------------------------------------- consultas a Voice URL --

export type VoiceURLOutcome =
  | "ok"
  | "timeout"
  | "network_error"
  | "http_error"
  | "invalid_markup"
  | "blocked"
  | "too_large";

export type VoiceURLRequest = {
  id: string;
  target: "primary" | "fallback";
  /** Sin query: puede llevar un token del tenant. */
  url: string;
  method: "POST" | "GET";
  status_code: number | null;
  latency_ms: number;
  outcome: VoiceURLOutcome;
  error: string | null;
  /** La secuencia de verbos del documento, en orden. */
  verbs: string[];
  created_at: string;
};

// -------------------------------------------------- media streams ----------

export type MediaStreamStatus =
  | "authorized"
  | "connecting"
  | "active"
  | "stopped"
  | "failed";

export type MediaStreamCloseReason =
  | "CALL_ENDED"
  | "TENANT_CLOSED"
  | "STREAM_CONNECT_FAILED"
  | "GATEWAY_SHUTDOWN"
  | "PROTOCOL_VIOLATION"
  | "MAX_DURATION_REACHED"
  | "AUTHORIZATION_EXPIRED"
  | "INTERNAL_ERROR";

export type MediaStream = {
  id: string;
  attempt_id: string;
  /** Sin query: un token en la query es una forma habitual de autenticar un WebSocket. */
  ws_url: string;
  audio_format: "audio/x-mulaw" | "audio/pcm16le";
  track: "inbound" | "outbound" | "both";
  status: MediaStreamStatus;
  started_at?: string;
  stopped_at?: string;
  created_at: string;
  stream_seconds: number;
  frames_in: number;
  frames_out: number;
  frames_dropped: number;
  close_code?: number;
  close_reason?: MediaStreamCloseReason;
};

// ---------------------------------------------- validación de markup -------

export type MarkupValidation = {
  valid: boolean;
  error?: {
    code: string;
    message: string;
    line: number;
    column: number;
  };
  verbs?: string[];
  warnings?: {
    verb: string;
    attribute: string;
    message: string;
    line: number;
  }[];
  /** Verbos que parsean y todavía no se ejecutan (fases 4 y 5). */
  not_yet_executable?: string[];
};

// ------------------------------------------------- saldo y consumo (fase 6) ---

// Los importes llegan en micro-MXN **y** en decimal. Se usan los dos: el entero
// para sumar y comparar, el decimal para mostrar. Sumar decimales en el cliente
// es como se pierden los centavos.
export type Balance = {
  currency: string;
  balance_micros: number;
  balance: string;
  reserved_micros: number;
  reserved: string;
  available_micros: number;
  available: string;
  low_balance_threshold_micros?: number;
  low_balance_threshold?: string;
};

export type BalanceTransactionKind =
  | "credit"
  | "reservation"
  | "reservation_release"
  | "charge"
  | "adjustment";

export type BalanceTransaction = {
  id: string;
  kind: BalanceTransactionKind;
  amount_micros: number;
  amount: string;
  balance_after_micros: number;
  balance_after: string;
  resource_type?: string;
  resource_id?: string;
  reason: string;
  created_at: string;
};

export type UsageRow = {
  day: string;
  destination: string;
  calls: number;
  answered_seconds: number;
  billable_seconds: number;
  charged_micros: number;
  charged: string;
};

export type Usage = {
  currency: string;
  timezone: string;
  from: string;
  to: string;
  total_micros: number;
  total: string;
  rows: UsageRow[];
};

// ------------------------------------------------ límites y antifraude --------

export type OutboundPolicy = {
  id: string;
  trunk_name: string;
  priority: number;
  status: string;
  prefix_allow: string[];
  prefix_deny: string[];
  max_cps: number;
  max_concurrent: number;
  max_call_seconds: number;
  daily_spend_limit_cents: number | null;
  allowed_from_minute: number;
  allowed_to_minute: number;
  allowed_weekdays: number[];
  allow_international: boolean;
  home_country_code: string;
  max_failure_percent: number | null;
  spike_factor: number | null;
  on_anomaly: "alert" | "suspend";
};

export type TenantLimits = {
  timezone: string;
  status: string;
  kill_switch: boolean;
  suspended_reason: string | null;
  cdr_retention_days: number;
  recording_retention_days: number;
  stream_retention_days: number;
  event_retention_days: number;
  /** Qué plazos están sin límite. `0` días significa sin límite. */
  unlimited?: {
    cdr: boolean;
    stream: boolean;
    event: boolean;
    recordings: boolean;
  };
  policies: OutboundPolicy[];
};

export type Exclusion = {
  e164: string;
  reason: string;
  created_at: string;
};

// ------------------------------------------------------------- tarifas --------

export type RateDeck = {
  id: string;
  name: string;
  description: string;
  currency: string;
  is_default: boolean;
  active_rates?: number;
  created_at: string;
};

export type Rate = {
  id: string;
  kind: string;
  prefix: string;
  catch_all: boolean;
  description: string;
  price_micros: number;
  price: string;
  initial_increment_seconds: number;
  subsequent_increment_seconds: number;
  connection_charge_micros: number;
  effective_from: string;
  effective_to: string | null;
};

export type ImportSummary = {
  added: number;
  changed: number;
  unchanged: number;
  closed: number;
  dry_run: boolean;
  changes: {
    prefix: string;
    kind: "added" | "changed";
    old_price?: string;
    new_price: string;
  }[];
};
